import { useState, useRef, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { NAVY, ACCENT, THEMES, useIsMobile } from "./ThemeContext";

const API = "/api";

export default function LoginScreen() {
  const { login } = useAuth();
  const mobile = useIsMobile();
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("phone");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [method, setMethod] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const countryCode = "+372";
  const codeRef = useRef(null);
  const t = THEMES.dark;

  useEffect(() => {
    if (step === 2 && codeRef.current) codeRef.current.focus();
  }, [step]);

  const getIdentifier = () => mode === "phone" ? `${countryCode}${phone.trim()}` : email.trim();
  const hasInput = mode === "phone" ? phone.trim().length >= 7 : email.trim().includes("@");

  const requestCode = async () => {
    setError(null);
    setSending(true);
    try {
      const res = await fetch(`${API}/auth/request-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: getIdentifier() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Viga");
      setMethod(data.method);
      setStep(2);
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    setError(null);
    setSending(true);
    try {
      await login(getIdentifier(), code.trim());
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "14px 16px", fontSize: 16,
    border: `1px solid ${t.border}`, borderRadius: 10, background: t.panel,
    color: t.text, fontFamily: "inherit", outline: "none",
  };
  const btnStyle = {
    width: "100%", padding: "14px", fontSize: 15, fontWeight: 700, border: "none",
    borderRadius: 10, cursor: "pointer", fontFamily: "inherit", letterSpacing: 0.5,
    background: ACCENT, color: "#fff",
  };
  const cardWidth = mobile ? "100%" : 380;

  return (
    <div style={{
      minHeight: "100vh", background: NAVY, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", padding: mobile ? 20 : 40,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12, background: "rgba(255,255,255,0.14)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: 22, color: "#fff", marginBottom: 12,
        }}>S</div>
        <div style={{ color: "#fff", fontWeight: 800, fontSize: 18, letterSpacing: 2 }}>STEINMANN OÜ</div>
        <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, letterSpacing: 1, marginTop: 4 }}>LogSite</div>
      </div>

      <div style={{
        width: cardWidth, background: t.panel, borderRadius: 16, padding: mobile ? 24 : 32,
        boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
      }}>
        {step === 1 ? (
          <>
            <div style={{ fontSize: 15, fontWeight: 600, color: t.text, marginBottom: 4 }}>Logi sisse</div>
            <div style={{ display: "flex", gap: 0, marginBottom: 16, borderRadius: 8, overflow: "hidden", border: `1px solid ${t.border}` }}>
              <button onClick={() => setMode("phone")}
                style={{ flex: 1, padding: "10px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer",
                  background: mode === "phone" ? ACCENT : t.panel, color: mode === "phone" ? "#fff" : t.muted }}>
                Telefon
              </button>
              <button onClick={() => setMode("email")}
                style={{ flex: 1, padding: "10px", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer",
                  background: mode === "email" ? ACCENT : t.panel, color: mode === "email" ? "#fff" : t.muted }}>
                E-post
              </button>
            </div>
            {mode === "phone" ? (
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ ...inputStyle, width: 72, flexShrink: 0, textAlign: "center", color: t.muted, fontSize: 15 }}>
                  +372
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={e => { if (e.key === "Enter" && hasInput) requestCode(); }}
                  placeholder="5551234"
                  autoFocus
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
            ) : (
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && hasInput) requestCode(); }}
                placeholder="email@näide.ee"
                autoFocus
                style={inputStyle}
              />
            )}
            {error && <div style={{ color: t.error, fontSize: 12.5, marginTop: 10 }}>{error}</div>}
            <button onClick={requestCode} disabled={!hasInput || sending}
              style={{ ...btnStyle, marginTop: 16, opacity: (!hasInput || sending) ? 0.5 : 1 }}>
              {sending ? "Saadan..." : "Saada kood"}
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 15, fontWeight: 600, color: t.text, marginBottom: 4 }}>Sisesta kood</div>
            <div style={{ fontSize: 12.5, color: t.muted, marginBottom: 20 }}>
              {method === "sms" ? "Kood saadeti SMS-iga" : method === "whatsapp" ? "Kood saadeti WhatsAppi" : "Kood saadeti e-postile"}: <strong style={{ color: t.text }}>{getIdentifier()}</strong>
            </div>
            <input
              ref={codeRef}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
              onKeyDown={e => { if (e.key === "Enter" && code.length === 6) verifyCode(); }}
              placeholder="000000"
              style={{ ...inputStyle, fontSize: 28, textAlign: "center", letterSpacing: 8, fontWeight: 700 }}
            />
            {error && <div style={{ color: t.error, fontSize: 12.5, marginTop: 10 }}>{error}</div>}
            <button onClick={verifyCode} disabled={code.length !== 6 || sending}
              style={{ ...btnStyle, marginTop: 16, opacity: (code.length !== 6 || sending) ? 0.5 : 1 }}>
              {sending ? "Kontrollin..." : "Logi sisse"}
            </button>
            <button onClick={() => { setStep(1); setCode(""); setError(null); }}
              style={{ ...btnStyle, marginTop: 8, background: "transparent", color: t.muted, fontSize: 13 }}>
              ← Tagasi
            </button>
          </>
        )}
      </div>
    </div>
  );
}
